import React, { useEffect, useState } from "react";
import axios from "axios";
import ReactQuill from "react-quill-new";
import {
  Briefcase, Eye, EyeOff, Loader2, Lock, Pencil, Plus, Trash2, X,
} from "lucide-react";
import { API } from "../../lib/api";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { useToast } from "../../hooks/use-toast";

const QUILL = { toolbar: [["bold", "italic", "underline", "link"], [{ list: "ordered" }, { list: "bullet" }], ["clean"]] };

const STATUS_META = {
  draft: { label: "Entwurf", cls: "bg-zinc-500/10 text-zinc-400" },
  open: { label: "Offen", cls: "bg-emerald-500/10 text-emerald-300" },
  closed: { label: "Geschlossen", cls: "bg-rose-500/10 text-rose-300" },
};

const EMPTY = { title: "", category: "Allgemein", short_description: "", description_html: "", requirements: "", responsibilities: "", status: "draft" };

const JobsPanel = ({ token }) => {
  const { toast } = useToast();
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [jobs, setJobs] = useState(null);
  const [editing, setEditing] = useState(null); // null | {form, id?}
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/cms/jobs`, auth);
      setJobs(r.data);
    } catch { setJobs([]); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openEditor = (job = null) => {
    if (!job) { setEditing({ form: { ...EMPTY } }); return; }
    setEditing({
      id: job.id,
      form: {
        title: job.title, category: job.category || "Allgemein",
        short_description: job.short_description || "",
        description_html: job.description_html || "",
        requirements: (job.requirements || []).join("\n"),
        responsibilities: (job.responsibilities || []).join("\n"),
        status: job.status || "draft",
      },
    });
  };

  const set = (k, v) => setEditing((e) => ({ ...e, form: { ...e.form, [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      const f = editing.form;
      const body = {
        title: f.title.trim(), category: f.category.trim() || "Allgemein",
        short_description: f.short_description.trim(),
        description_html: f.description_html,
        requirements: f.requirements.split("\n").map((s) => s.trim()).filter(Boolean),
        responsibilities: f.responsibilities.split("\n").map((s) => s.trim()).filter(Boolean),
        status: f.status,
      };
      if (editing.id) await axios.put(`${API}/cms/jobs/${editing.id}`, body, auth);
      else await axios.post(`${API}/cms/jobs`, body, auth);
      toast({ title: editing.id ? "Job aktualisiert" : "Job erstellt" });
      setEditing(null);
      await load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast({ title: "Fehler", description: typeof d === "string" ? d : "Speichern fehlgeschlagen", variant: "destructive" });
    } finally { setSaving(false); }
  };

  const quickStatus = async (job, status) => {
    try {
      await axios.put(`${API}/cms/jobs/${job.id}`, {
        title: job.title, category: job.category || "Allgemein",
        short_description: job.short_description || "",
        description_html: job.description_html || "",
        requirements: job.requirements || [], responsibilities: job.responsibilities || [],
        status,
      }, auth);
      await load();
    } catch { toast({ title: "Fehler", description: "Status-Wechsel fehlgeschlagen", variant: "destructive" }); }
  };

  const remove = async (job) => {
    if (!window.confirm(`Job "${job.title}" wirklich löschen?`)) return;
    try {
      await axios.delete(`${API}/cms/jobs/${job.id}`, auth);
      toast({ title: "Job gelöscht" });
      await load();
    } catch { toast({ title: "Fehler", description: "Löschen fehlgeschlagen", variant: "destructive" }); }
  };

  if (jobs === null) return <div className="flex justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-zinc-500" /></div>;

  return (
    <div data-testid="jobs-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="flex items-center gap-2 text-xl font-bold text-white"><Briefcase className="h-5 w-5 text-[#9d7bff]" /> Jobs</h2>
          <p className="mt-0.5 text-xs text-zinc-500">Nur Jobs mit Status „Offen" sind öffentlich sichtbar unter /jobs.</p>
        </div>
        <Button onClick={() => openEditor()} data-testid="jobs-new-btn" className="btn-primary rounded-lg px-4 py-2 text-sm font-semibold">
          <Plus className="mr-1.5 h-4 w-4" /> Neuer Job
        </Button>
      </div>

      {/* Editor */}
      {editing && (
        <div className="mt-5 rounded-2xl border border-[#7c4dff]/30 bg-[#12101f] p-5" data-testid="jobs-editor">
          <div className="flex items-center justify-between">
            <div className="text-sm font-bold text-white">{editing.id ? "Job bearbeiten" : "Neuer Job"}</div>
            <button onClick={() => setEditing(null)} data-testid="jobs-editor-close" className="text-zinc-500 hover:text-white"><X className="h-4 w-4" /></button>
          </div>
          <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
            <Input data-testid="jobs-editor-title" value={editing.form.title} onChange={(e) => set("title", e.target.value)} placeholder="Titel (z.B. Redakteur:in)" className="bg-[#10141b] border-white/10 text-zinc-200" />
            <Input data-testid="jobs-editor-category" value={editing.form.category} onChange={(e) => set("category", e.target.value)} placeholder="Kategorie (z.B. Redaktion)" className="bg-[#10141b] border-white/10 text-zinc-200" />
          </div>
          <textarea data-testid="jobs-editor-short" value={editing.form.short_description} onChange={(e) => set("short_description", e.target.value)} rows={2} placeholder="Kurzbeschreibung für die Job-Karte…" className="mt-3 w-full rounded-lg border border-white/10 bg-[#10141b] p-3 text-sm text-zinc-200 outline-none focus:border-[#9d7bff]/50" />
          <div className="mt-3 rounded-lg border border-white/10 bg-[#10141b]" data-testid="jobs-editor-description">
            <ReactQuill theme="snow" value={editing.form.description_html} onChange={(v) => set("description_html", v)} modules={QUILL} placeholder="Ausführliche Beschreibung…" />
          </div>
          <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Aufgabenbereich (eine pro Zeile)</div>
              <textarea data-testid="jobs-editor-responsibilities" value={editing.form.responsibilities} onChange={(e) => set("responsibilities", e.target.value)} rows={5} className="w-full rounded-lg border border-white/10 bg-[#10141b] p-3 text-sm text-zinc-200 outline-none focus:border-[#9d7bff]/50" />
            </div>
            <div>
              <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Anforderungen (eine pro Zeile)</div>
              <textarea data-testid="jobs-editor-requirements" value={editing.form.requirements} onChange={(e) => set("requirements", e.target.value)} rows={5} className="w-full rounded-lg border border-white/10 bg-[#10141b] p-3 text-sm text-zinc-200 outline-none focus:border-[#9d7bff]/50" />
            </div>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <select data-testid="jobs-editor-status" value={editing.form.status} onChange={(e) => set("status", e.target.value)} className="h-9 rounded-md border border-white/10 bg-[#10141b] px-3 text-sm text-zinc-200 outline-none">
              <option value="draft">Entwurf</option>
              <option value="open">Offen (öffentlich)</option>
              <option value="closed">Geschlossen</option>
            </select>
            <Button onClick={save} disabled={saving || !editing.form.title.trim()} data-testid="jobs-editor-save" className="btn-primary rounded-lg px-5 py-2 text-sm font-semibold">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Speichern
            </Button>
          </div>
        </div>
      )}

      {/* Liste */}
      <div className="mt-5 space-y-2">
        {jobs.length === 0 && <div className="rounded-xl border border-white/10 bg-[#0f141b] px-4 py-10 text-center text-sm text-zinc-500" data-testid="jobs-list-empty">Noch keine Jobs angelegt.</div>}
        {jobs.map((j) => {
          const meta = STATUS_META[j.status] || STATUS_META.draft;
          return (
            <div key={j.id} className="flex flex-wrap items-center gap-3 rounded-xl border border-white/10 bg-[#0f141b] px-4 py-3" data-testid={`jobs-row-${j.id}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate text-sm font-semibold text-white">{j.title}</span>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${meta.cls}`}>{meta.label}</span>
                </div>
                <div className="text-[11px] text-zinc-500">{j.category} · {j.application_count} Bewerbung(en)</div>
              </div>
              {j.status === "open" ? (
                <Button onClick={() => quickStatus(j, "draft")} data-testid={`jobs-unpublish-${j.id}`} title="Depublizieren" className="btn-ghost rounded-lg px-2.5 py-1.5 text-xs"><EyeOff className="h-3.5 w-3.5" /></Button>
              ) : (
                <Button onClick={() => quickStatus(j, "open")} data-testid={`jobs-publish-${j.id}`} title="Veröffentlichen" className="btn-ghost rounded-lg px-2.5 py-1.5 text-xs"><Eye className="h-3.5 w-3.5" /></Button>
              )}
              {j.status !== "closed" && (
                <Button onClick={() => quickStatus(j, "closed")} data-testid={`jobs-close-${j.id}`} title="Schließen" className="btn-ghost rounded-lg px-2.5 py-1.5 text-xs"><Lock className="h-3.5 w-3.5" /></Button>
              )}
              <Button onClick={() => openEditor(j)} data-testid={`jobs-edit-${j.id}`} className="btn-ghost rounded-lg px-2.5 py-1.5 text-xs"><Pencil className="h-3.5 w-3.5" /></Button>
              <Button onClick={() => remove(j)} data-testid={`jobs-delete-${j.id}`} className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-2.5 py-1.5 text-xs text-rose-300 hover:bg-rose-500/20"><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default JobsPanel;
